// src/api/axiosClient.js
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { trackDebugEvent, trackAnalyticsEvent } from '../loggerService';
import { getUserLocation } from '../utils/getUserLocation';
import config from '../config'; // import the configuration

// Create a new Axios instance with a base URL
const axiosClient = axios.create({
  baseURL: config.API_URL,
});

/**
 * Request Interceptor
 *  - Attach the Supabase authorization token
 *  - Optionally attach user location
 *  - Track debug and analytics events
 *  - Store a `metadata` object for performance timing
 */
axiosClient.interceptors.request.use(
  async (configAxios) => {
    // Retrieve session from Supabase
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.access_token) {
      configAxios.headers.Authorization = `Bearer ${session.access_token}`;
    }

    // Uncomment if you want to attach geolocation headers
    // try {
    //   const location = await getUserLocation();
    //   configAxios.headers['X-User-Latitude'] = location.latitude;
    //   configAxios.headers['X-User-Longitude'] = location.longitude;
    // } catch (err) {
    //   console.warn('Geolocation failed or was denied:', err.message);
    // }

    // Store metadata to measure duration
    configAxios.metadata = { startTime: performance.now() };

    let requestDataString = '';
    try {
      requestDataString = JSON.stringify(configAxios.data, null, 2);
    } catch (err) {
      requestDataString = '[Unable to stringify data]';
    }

    // Debug: request sent
    trackDebugEvent('axios_request_sent', {
      url: configAxios.baseURL + configAxios.url,
      method: configAxios.method,
      data: requestDataString,
    });

    // Analytics: request initiated
    trackAnalyticsEvent('api_request_initiated', {
      url: configAxios.url,
      method: configAxios.method,
      timestamp: Date.now(),
    });

    return configAxios;
  },
  (error) => {
    // This handles a request setup error
    trackDebugEvent('axios_request_error', { error: error.message });
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *  - Measure duration
 *  - Log status code, data
 *  - Handle errors with a second interceptor param
 */
axiosClient.interceptors.response.use(
  (response) => {
    // Extract metadata for duration measurement
    const { metadata } = response.config;
    const duration = metadata ? performance.now() - metadata.startTime : 0;

    // Debug: request received
    trackDebugEvent('axios_request_received', {
      url: response.config.url,
      status: response.status,
      duration,
      responseData: response.data,
    });

    // Analytics: request completed
    trackAnalyticsEvent('api_request_completed', {
      url: response.config.url,
      status: response.status,
      duration,
      timestamp: Date.now(),
    });

    return response;
  },
  async (error) => {
    // If we get a response, we can still parse some info
    const { response, config } = error;
    const { metadata } = config || {};

    // Duration if we had a startTime
    const duration = metadata ? performance.now() - metadata.startTime : 0;

    // Debug: error details
    trackDebugEvent('axios_request_failed', {
      url: config?.url,
      status: response?.status,
      duration,
      error: error.message,
      responseData: response?.data,
    });

    // We can optionally track an analytics event for failures
    // trackAnalyticsEvent('api_request_failed', { ... });

    return Promise.reject(error);
  }
);

export default axiosClient;
